import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField, MatHint, MatLabel, MatOption, MatSelect } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { ProgramPartnerService } from '@k12/admin-services';
import { MatInput } from '@angular/material/input';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageCenterService } from '@k12/messaging';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastMessageService, AppInsightsService, SchoolsService } from '@k12/app-services';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MsalService } from '@azure/msal-angular';
import { 
  MessageCenterCategory, 
  MessageCenterChildCategory, 
  SendAccountMessagePayload, 
  ProgramPartner, 
  SchoolContact,
  ToastMessageType 
} from '@k12/models';

type QuickChatRecipientRole = 'hdma' | 'primaryContact' | 'contact';

interface QuickChatRecipientOption {
  id: string;
  name: string;
  role: QuickChatRecipientRole;
  isLastUpdatedBy: boolean;
}

const RECIPIENT_ROLE_LABELS: Record<QuickChatRecipientRole, string> = {
  hdma: 'HDMA',
  primaryContact: 'Primary Contact',
  contact: 'Contact'
};

const RECIPIENT_ROLE_SORT_ORDER: Record<QuickChatRecipientRole, number> = {
  primaryContact: 0,
  hdma: 1,
  contact: 2
};

const LAST_UPDATED_BY_LABEL = 'Last Updated By';

export interface MessageCenterSendDialogData {
  programPartnerId: string;
  programPartnerTypeId: number | null;
  taskName?: string;
  recipientId?: string | null;
  recipientName?: string | null;
  hdmaId: string | null;
  hdmaName?: string | null;
  lastUpdatedByAccountId?: string | null;
  lastUpdatedByName?: string | null;
  allowRecipientSelection?: boolean;
  // Adds the school's other contacts to the selectable recipients (only when selection is allowed).
  includeSchoolContacts?: boolean;
}

const ERROR_SENDING_ACCOUNT_MESSAGE = 'Error sending account message';
// A missing account id can arrive as an all-zeros GUID. It is a non-empty string, so it has to be
// rejected explicitly or it is treated as a real recipient.
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
// Mirrors the Contacts table, which disables messaging for archived contacts.
const ARCHIVED_CONTACT_STATUS_ID = 3;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-message-center-send-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatOption,
    MatSelect,
    MatFormField,
    MatIconModule,
    MatInput,
    MatLabel,
    TextFieldModule,
    MatTooltipModule,
    ReactiveFormsModule,
    FormsModule,
    MatHint,
    MatProgressBar
  ],
  templateUrl: './message-center-send-dialog.component.html',
  styleUrl: './message-center-send-dialog.component.scss'
})
export class MessageCenterSendDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<MessageCenterSendDialogComponent>);
  readonly data = inject<MessageCenterSendDialogData>(MAT_DIALOG_DATA);
  readonly programPartnerService = inject(ProgramPartnerService);
  readonly messageService = inject(MessageCenterService);
  readonly toastService = inject(ToastMessageService);
  readonly msalService = inject(MsalService);
  readonly appInsightsService = inject(AppInsightsService);
  readonly schoolsService = inject(SchoolsService);

  programPartner: ProgramPartner = {} as ProgramPartner;
  categories: MessageCenterCategory[] = [];
  subCategories: MessageCenterChildCategory[] = [];

  selectedCategory: MessageCenterCategory | null = null;
  selectedSubcategory: MessageCenterChildCategory | null = null;
  messageContent: string = '';
  recipientOptions: QuickChatRecipientOption[] = [];
  selectedRecipientId: string | null = null;
  sendingMessage: boolean = false;
  accountId: string = '';

  private schoolContactOptions: QuickChatRecipientOption[] = [];
  private hasUserSelectedRecipient = false;

  constructor() {
    this.accountId = this.msalService.instance.getActiveAccount()?.localAccountId ?? '';
    this.selectedRecipientId = this.normalizeValue(this.data.recipientId) ?? this.normalizeValue(this.data.hdmaId);
  }

  ngOnInit(): void {
    this.initializeRecipientOptions();
    this.getProgramPartner();
    this.getSchoolContacts();
    this.getTopics();
  }

  categoryChange() {
    this.subCategories = this.selectedCategory?.childCategories ?? [];
    this.selectedSubcategory =
      this.subCategories.find((subCategory) => subCategory.id === this.selectedSubcategory?.id) ?? null;
  }

  getTopics() {
    this.messageService.GetMessageTopicsForProgramPartnerType(this.data.programPartnerTypeId).subscribe({
      next: (res) => {
        this.categories = res;
        this.categoryChange();
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  getProgramPartner() {
    this.programPartnerService.getProgramPartner(this.data.programPartnerId).subscribe({
      next: (res) => {
        this.programPartner = res.body ?? ({} as ProgramPartner);
        this.initializeRecipientOptions();
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  getSchoolContacts() {
    if (!this.data.includeSchoolContacts || !this.isRecipientSelectionEnabled) {
      return;
    }

    this.schoolsService.getSchoolContacts(this.data.programPartnerId).subscribe({
      next: (res) => {
        this.schoolContactOptions = (res?.entity ?? [])
          .map((contact) => this.toSchoolContactOption(contact))
          .filter((option): option is QuickChatRecipientOption => option !== null);
        this.initializeRecipientOptions();
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  onKeyUp(event: KeyboardEvent) {
    if ((event.key === 'Enter' || event.key === 'Return') && this.canSendMessage) {
      this.btnSendMessage();
    }
  }

  btnSendMessage() {
    const recipientId = this.normalizeValue(this.selectedRecipientId) ?? this.resolveHdmaRecipientId();
    if (!recipientId) {
      this.toastService.showToast(ToastMessageType.Error, ERROR_SENDING_ACCOUNT_MESSAGE);
      this.sendingMessage = false;
      return;
    }

    this.sendingMessage = true;
    const selectedSubcategory =
      this.subCategories.find((subCategory) => subCategory.id === this.selectedSubcategory?.id) ?? null;
    const childCategoryId = selectedSubcategory?.id;
    const description = selectedSubcategory?.description ?? this.selectedCategory?.description ?? '';

    const topicPayload = {
      programPartnerId: this.data.programPartnerId,
      programPartnerTypeId: this.data.programPartnerTypeId ?? this.programPartner?.programPartnerTypeId ?? null,
      parentCategoryId: this.selectedCategory?.id,
      parentCategoryName: this.selectedCategory?.name,
      childCategoryName: selectedSubcategory?.name ?? '',
      description,
      Description: description,
      participantId: recipientId,
      assignedToId: this.accountId,
      ...(childCategoryId ? { childCategoryId } : {})
    };

    this.messageService.CreateAdminAccountMessageTopic(topicPayload).subscribe({
      next: (topic) => {
        const accountMessageTopicId = this.resolveCreatedTopicId(topic);
        if (!accountMessageTopicId) {
          this.toastService.showToast(ToastMessageType.Error, 'Error creating account message topic');
          this.sendingMessage = false;
          return;
        }

        const sendMessagePayload: SendAccountMessagePayload = {
          message: this.messageContent,
          accountMessageTopicId,
          recipientId: recipientId,
          senderId: this.accountId
        };
        this.messageService.SendAdminAccountMessage(sendMessagePayload).subscribe({
          next: (result) => {
            if (this.isFailedApiResult(result)) {
              this.toastService.showToast(ToastMessageType.Error, ERROR_SENDING_ACCOUNT_MESSAGE);
              this.sendingMessage = false;
              return;
            }
            this.messageContent = '';
            this.dialogRef.close(true);
          },
          error: () => {
            this.toastService.showToast(ToastMessageType.Error, ERROR_SENDING_ACCOUNT_MESSAGE);
            this.sendingMessage = false;
          }
        });
      },
      error: () => {
        this.toastService.showToast(ToastMessageType.Error, 'Error creating account message topic');
        this.sendingMessage = false;
      }
    });
  }

  get isCategoryValid(): boolean {
    return this.selectedCategory != null && (this.subCategories.length !== 0 ? this.selectedSubcategory != null : true);
  }

  get canSendMessage(): boolean {
    const messageContentIsValid = this.messageContent.length > 0 && this.messageContent.length <= 500;
    return this.isCategoryValid && messageContentIsValid && !!this.selectedRecipientId;
  }

  get canSelectRecipient(): boolean {
    return this.isRecipientSelectionEnabled;
  }

  onRecipientSelectionChange(): void {
    this.hasUserSelectedRecipient = true;
  }

  getRecipientLabel(recipientOption: QuickChatRecipientOption): string {
    return `${recipientOption.name} - ${this.getRecipientRoleLabel(recipientOption)}`;
  }

  private getRecipientRoleLabel(recipientOption: QuickChatRecipientOption): string {
    if (!recipientOption.isLastUpdatedBy) {
      return RECIPIENT_ROLE_LABELS[recipientOption.role];
    }

    return recipientOption.role === 'contact'
      ? LAST_UPDATED_BY_LABEL
      : `${RECIPIENT_ROLE_LABELS[recipientOption.role]}, ${LAST_UPDATED_BY_LABEL}`;
  }

  private initializeRecipientOptions(): void {
    const hdmaRecipientId = this.resolveHdmaRecipientId();
    const hdmaOption: QuickChatRecipientOption | null = hdmaRecipientId
      ? {
          id: hdmaRecipientId,
          name: this.resolveHdmaRecipientName(hdmaRecipientId),
          role: 'hdma',
          isLastUpdatedBy: false
        }
      : null;

    if (!this.isRecipientSelectionEnabled) {
      const lockedOption = this.resolveLockedRecipientOption(hdmaOption);
      this.recipientOptions = lockedOption ? [lockedOption] : [];
      this.selectedRecipientId = lockedOption?.id ?? null;
      return;
    }

    const lastUpdatedByAccountId = this.normalizeValue(this.data.lastUpdatedByAccountId);
    const previousSelectedRecipientId = this.selectedRecipientId;
    const options: QuickChatRecipientOption[] = hdmaOption ? [hdmaOption] : [];

    for (const contactOption of this.schoolContactOptions) {
      if (!options.some((option) => option.id === contactOption.id)) {
        options.push(contactOption);
      }
    }

    if (lastUpdatedByAccountId && !options.some((option) => option.id === lastUpdatedByAccountId)) {
      options.push({
        id: lastUpdatedByAccountId,
        name: this.resolveLastUpdatedByName(lastUpdatedByAccountId),
        role: 'contact',
        isLastUpdatedBy: false
      });
    }

    // Array.sort is stable, so options keep their API order within each role.
    this.recipientOptions = options
      .map((option) => ({ ...option, isLastUpdatedBy: option.id === lastUpdatedByAccountId }))
      .sort((a, b) => RECIPIENT_ROLE_SORT_ORDER[a.role] - RECIPIENT_ROLE_SORT_ORDER[b.role]);
    // Default to the first option (Primary Contact when present) until the user picks a recipient.
    this.selectedRecipientId =
      this.hasUserSelectedRecipient &&
      previousSelectedRecipientId &&
      this.recipientOptions.some((option) => option.id === previousSelectedRecipientId)
        ? previousSelectedRecipientId
        : this.recipientOptions[0]?.id ?? null;
  }

  private resolveLockedRecipientOption(hdmaOption: QuickChatRecipientOption | null): QuickChatRecipientOption | null {
    const recipientId = this.normalizeValue(this.data.recipientId);
    if (!recipientId || recipientId === hdmaOption?.id) {
      return hdmaOption;
    }

    return {
      id: recipientId,
      name: this.normalizeValue(this.data.recipientName) ?? recipientId,
      role: 'contact',
      isLastUpdatedBy: false
    };
  }

  private toSchoolContactOption(contact: SchoolContact): QuickChatRecipientOption | null {
    const accountId = this.normalizeValue(contact.accountId);
    if (!accountId || contact.statusId === ARCHIVED_CONTACT_STATUS_ID) {
      return null;
    }

    const contactName = [contact.person?.firstName, contact.person?.lastName]
      .filter((name): name is string => !!name)
      .join(' ')
      .trim();

    return {
      id: accountId,
      name: contactName || accountId,
      role: this.resolveSchoolContactRole(contact),
      isLastUpdatedBy: false
    };
  }

  private resolveSchoolContactRole(contact: SchoolContact): QuickChatRecipientRole {
    if (contact.isHDMA) {
      return 'hdma';
    }

    return contact.isPrimaryContact ? 'primaryContact' : 'contact';
  }

  private get isRecipientSelectionEnabled(): boolean {
    if (this.data.allowRecipientSelection !== undefined) {
      return this.data.allowRecipientSelection;
    }

    return !!this.normalizeValue(this.data.taskName);
  }

  private resolveHdmaRecipientId(): string | null {
    // Normalize each candidate before falling through: `??` only skips null, so an all-zeros id would
    // otherwise stop the chain before the program partner's account is ever considered.
    return this.normalizeValue(this.data.hdmaId) ?? this.normalizeValue(this.programPartner?.accountId);
  }

  private resolveCreatedTopicId(topic: unknown): string | null {
    if (!topic || typeof topic !== 'object' || this.isFailedApiResult(topic)) {
      return null;
    }

    const record = topic as Record<string, unknown>;
    const entity = record['entity'];
    const entityRecord = entity && typeof entity === 'object' ? (entity as Record<string, unknown>) : undefined;
    const id =
      record['id'] ??
      entityRecord?.['id'] ??
      record['accountMessageTopicId'] ??
      entityRecord?.['accountMessageTopicId'];

    return id != null ? this.normalizeValue(String(id)) : null;
  }

  private isFailedApiResult(result: unknown): boolean {
    return !!result && typeof result === 'object' && (result as { success?: unknown }).success === false;
  }

  private normalizeValue(value: string | null | undefined): string | null {
    const trimmedValue = value?.trim();
    return trimmedValue && trimmedValue !== EMPTY_GUID ? trimmedValue : null;
  }

  private resolveHdmaRecipientName(hdmaRecipientId: string): string {
    return (
      this.normalizeValue(this.data.hdmaName) ??
      this.normalizeValue(this.programPartner?.hdmaName) ??
      this.normalizeValue(this.programPartner?.displayName) ??
      hdmaRecipientId
    );
  }

  private resolveLastUpdatedByName(lastUpdatedByAccountId: string): string {
    return this.normalizeValue(this.data.lastUpdatedByName) ?? lastUpdatedByAccountId;
  }
}
